import React, { useEffect, useRef, useState } from 'react';
import { adicionarFornecedoresACotacao, fetchFornecedores } from '../../../api/portalCotacoes/portalCotacoesApi';
import { Fornecedor } from '../../../interfaces/PortalCotacoes';
import { notify } from '../../../shared/feedback/notify';
import FornecedorDetalhesModal from './FornecedorDetalhesModal';
import EscolhaDeItensModal, { ItemEscolhivel } from './EscolhaDeItensModal';
import '../../../components/modal/add-car/AddCarModal.css';
import './FornecedorDetalhesModal.css';
import '../../../shared/ui/modal-close-button.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/badge-status.css'; // selos "Já convidado" / "Sem WhatsApp", por import (BUG-V74)

/**
 * Convidar fornecedor numa cotação JÁ ENVIADA (portal-cotacoes/at-17).
 *
 * Pedido do teste de Compras de 29/10/2026: depois do envio não havia como incluir mais um
 * fornecedor, e o comprador refazia a cotação inteira. O convite sai pelo WhatsApp, como os
 * demais; fornecedor sem WhatsApp não pode ser escolhido (o servidor também recusa).
 *
 * 🔴 Falha da busca nunca vira "nenhum fornecedor encontrado" (at-60).
 *
 * Desde a at-18 o convite pode levar SÓ parte dos itens: é o "recotar só o que faltou" com a
 * cotação aberta (o item que ninguém ofertou vai a outro fornecedor, sem fechar a cotação).
 */

interface ConvidarFornecedorModalProps {
  cotacaoId: string;
  /** CNPJs já convidados: aparecem marcados e não podem ser escolhidos de novo. */
  jaConvidados: string[];
  /** Os itens da cotação (at-18): o convite pode levar só parte deles. */
  itens: ItemEscolhivel[];
  /** Itens já marcados ao abrir (os que estão sem oferta, quando vem da faixa). Ausente = todos. */
  itensIniciais?: string[];
  onFechar: () => void;
  onConvidados: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
}

const TAMANHO_DA_BUSCA = 30;

const temWhatsapp = (f: Fornecedor) => f.contatos.some((c) => Boolean(c.telefone));

const ConvidarFornecedorModal: React.FC<ConvidarFornecedorModalProps> = ({
  cotacaoId,
  jaConvidados,
  itens,
  itensIniciais,
  onFechar,
  onConvidados,
  sidebarRef,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [busca, setBusca] = useState('');
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [escolhidos, setEscolhidos] = useState<string[]>([]);
  const [enviando, setEnviando] = useState(false);
  /** Fornecedor cujo cadastro de contato está aberto por cima deste modal (mesmo fluxo do wizard). */
  const [emCadastro, setEmCadastro] = useState<Fornecedor | null>(null);
  const [itensEscolhidos, setItensEscolhidos] = useState<string[]>(() => itensIniciais ?? itens.map((i) => i.id));
  const [escolhendoItens, setEscolhendoItens] = useState(false);
  const todosOsItens = itensEscolhidos.length === itens.length;

  /** Depois de cadastrar o contato, relê o próprio fornecedor: o card passa a ser escolhível na hora. */
  async function recarregarFornecedor(cnpj: string) {
    try {
      const r = await fetchFornecedores({ search: cnpj, pageSize: TAMANHO_DA_BUSCA });
      const atualizado = r.data.find((f) => f.cnpj === cnpj);
      if (atualizado) {
        setFornecedores((atual) => atual.map((f) => (f.cnpj === cnpj ? atualizado : f)));
        setEmCadastro((atual) => (atual?.cnpj === cnpj ? atualizado : atual));
      }
    } catch {
      // O cadastro em si deu certo; só a recarga falhou. Dizer isso evita cadastrar de novo.
      notify.warning('O contato foi cadastrado, mas a lista não atualizou. Feche e abra o convite de novo.', {
        toastId: 'pc-convidar-recarga',
      });
    }
  }

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    const t = setTimeout(async () => {
      try {
        const r = await fetchFornecedores({ search: busca.trim() || undefined, pageSize: TAMANHO_DA_BUSCA });
        if (!ativo) return;
        setFornecedores(r.data.filter((f) => !f.inativo));
        setErro(false);
      } catch {
        if (ativo) setErro(true);
      } finally {
        if (ativo) setCarregando(false);
      }
    }, 300);
    return () => {
      ativo = false;
      clearTimeout(t);
    };
  }, [busca]);

  useEffect(() => {
    // Com o cadastro aberto por cima, o clique nele está fora deste modal: sem a guarda, o
    // primeiro clique no cadastro fecharia o convite inteiro (03-code-conventions.md).
    const travado = enviando || emCadastro !== null || escolhendoItens;
    const fora = (e: MouseEvent) => {
      if (travado) return;
      const alvo = e.target as Node;
      if (modalRef.current?.contains(alvo) || sidebarRef?.current?.contains(alvo)) return;
      onFechar();
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !travado) onFechar();
    };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [onFechar, sidebarRef, enviando, emCadastro, escolhendoItens]);

  const alternar = (cnpj: string) =>
    setEscolhidos((atual) => (atual.includes(cnpj) ? atual.filter((c) => c !== cnpj) : [...atual, cnpj]));

  async function convidar() {
    if (escolhidos.length === 0) return;
    setEnviando(true);
    try {
      await adicionarFornecedoresACotacao(cotacaoId, escolhidos, todosOsItens ? undefined : itensEscolhidos);
      notify.success(
        escolhidos.length === 1
          ? 'Fornecedor convidado. O convite foi enviado pelo WhatsApp.'
          : `${escolhidos.length} fornecedores convidados. Os convites foram enviados pelo WhatsApp.`,
      );
      onConvidados();
    } catch (error) {
      notify.errorFrom(error, 'Não foi possível convidar o fornecedor.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="add-car-modal-overlay">
      <div className="fornecedor-modal-content" ref={modalRef}>
        <div className="fornecedor-modal-header">
          <div className="fornecedor-modal-header-info">
            <h2>Convidar fornecedor</h2>
            <span className="fornecedor-modal-subtitulo">
              O convite sai pelo WhatsApp, com o mesmo prazo da cotação.
            </span>
          </div>
          <button className="close-button" onClick={onFechar} aria-label="Fechar modal" disabled={enviando}>
            &times;
          </button>
        </div>

        <div className="fornecedor-modal-body">
          <div className="fornecedor-campo">
            <input
              className="fornecedor-modal-input"
              placeholder="Buscar por razão social ou CNPJ"
              aria-label="Buscar fornecedor"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
            <p className="fornecedor-modal-hint" style={{ margin: 0 }}>
              Só fornecedor com WhatsApp cadastrado pode ser convidado; quem não tem, você cadastra aqui mesmo. Quem
              já está na cotação aparece marcado.
            </p>
          </div>

          <div className="fornecedor-campo" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              className="fornecedor-modal-btn"
              onClick={() => setEscolhendoItens(true)}
              disabled={enviando}
            >
              {todosOsItens ? `Itens: todos (${itens.length})` : `Itens: ${itensEscolhidos.length} de ${itens.length}`}
            </button>
            <span className="fornecedor-modal-hint" style={{ margin: 0 }}>
              O convite leva só estes itens.
            </span>
          </div>

          {erro ? (
            <p className="erro-de-tela">
              Não foi possível buscar os fornecedores. Tente de novo; se persistir, acione a TI.
            </p>
          ) : carregando ? (
            <p className="fornecedor-modal-vazio">Buscando...</p>
          ) : fornecedores.length === 0 ? (
            <p className="fornecedor-modal-vazio">Nenhum fornecedor ativo encontrado para esta busca.</p>
          ) : (
            // Altura limitada com rolagem própria: a lista traz até 30 e o modal não pode crescer com ela.
            <div className="fornecedor-contatos-lista" style={{ maxHeight: '50vh', overflowY: 'auto', marginBottom: 0, paddingRight: 14 }}>
              {fornecedores.map((f) => {
                const convidado = jaConvidados.includes(f.cnpj);
                const semWhats = !temWhatsapp(f);
                const bloqueado = convidado || semWhats;
                const marcado = convidado || escolhidos.includes(f.cnpj);
                return (
                  // A linha inteira é o rótulo da caixa: clicar no nome marca o fornecedor.
                  <label
                    key={f.cnpj}
                    className="fornecedor-contato-linha"
                    style={{ cursor: bloqueado ? 'default' : 'pointer' }}
                  >
                    {/* Só a parte do nome esmaece: o botão de cadastrar ao lado continua com cor de ação. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, opacity: bloqueado ? 0.55 : 1 }}>
                      <input
                        type="checkbox"
                        disabled={bloqueado || enviando}
                        checked={marcado}
                        onChange={() => alternar(f.cnpj)}
                        style={{ accentColor: '#ea5d0e', width: 16, height: 16, flexShrink: 0 }}
                      />
                      <div className="fornecedor-contato-dados">
                        <span
                          className="fornecedor-contato-nome"
                          title={f.razaoSocial}
                          style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {f.razaoSocial}
                        </span>
                        <span className="fornecedor-contato-canais">{f.cnpj}</span>
                      </div>
                    </div>
                    {convidado ? (
                      <span className="cilia-badge badge-verde" style={{ flexShrink: 0, opacity: 0.55 }}>Já convidado</span>
                    ) : semWhats ? (
                      // Cadastrar o WhatsApp sem sair do convite, no mesmo modal do wizard (pedido do Victor, 30/10/2026).
                      <button
                        type="button"
                        className="fornecedor-modal-btn"
                        style={{ height: 30, padding: '0 12px', fontSize: 12, flexShrink: 0, opacity: 1 }}
                        disabled={enviando}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setEmCadastro(f);
                        }}
                      >
                        Cadastrar WhatsApp
                      </button>
                    ) : null}
                  </label>
                );
              })}
            </div>
          )}

          <div className="fornecedor-modal-acoes" style={{ marginTop: 16 }}>
            <button type="button" className="fornecedor-modal-btn" onClick={onFechar} disabled={enviando}>
              Cancelar
            </button>
            <button
              type="button"
              className="fornecedor-modal-btn fornecedor-modal-btn--primario"
              onClick={convidar}
              disabled={enviando || escolhidos.length === 0}
            >
              {enviando
                ? 'Enviando...'
                : escolhidos.length === 0
                  ? 'Convidar'
                  : `Convidar ${escolhidos.length} fornecedor${escolhidos.length > 1 ? 'es' : ''}`}
            </button>
          </div>
        </div>
      </div>

      {/* `empilhado`: os dois overlays empatariam no z-index, e o empate se decide pela ordem do DOM (BUG-R97). */}
      {escolhendoItens && (
        <EscolhaDeItensModal
          titulo="Itens do convite"
          itens={itens}
          marcados={itensEscolhidos}
          onConfirmar={(ids) => {
            setItensEscolhidos(ids);
            setEscolhendoItens(false);
          }}
          onFechar={() => setEscolhendoItens(false)}
        />
      )}

      {emCadastro && (
        <FornecedorDetalhesModal
          fornecedor={emCadastro}
          empilhado
          onFechar={() => setEmCadastro(null)}
          onAtualizado={() => recarregarFornecedor(emCadastro.cnpj)}
        />
      )}
    </div>
  );
};

export default ConvidarFornecedorModal;
